import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages an insurance component.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox – The checkbox element.
 *
 * @extends Component<Refs>
 */

export class InsuranceComponent extends Component {
  requiredRefs = ['checkbox'];

  handleChange(event) {
    this.refs.checkbox.disabled = true;

    const insuranceProductId = this.dataset.insuranceProductId;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (event.target.checked) {
      const variantQuantity = Number(this.dataset.variantQuantity) || 1;
      const variantId = this.dataset.variantId;
      const insuranceVariantId = this.dataset.insuranceVariantId;
      const items = [];
      for (let i = 1; i <= variantQuantity; i++) {
        const ref = `ins-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        items.push({
          id: insuranceVariantId,
          quantity: 1,
          properties: {
            _insure: 'true',
            _insurer: this.dataset.insurer || '',
            _insurance_premium: this.dataset.unitPremium || '',
            _insurance_type: this.dataset.insuranceType || 'regular',
            _insured_product_title: this.dataset.insuredProductTitle || '',
            _insured_product_brand: this.dataset.insuredProductBrand || '',
            _insured_product_price: this.dataset.insuredProductPrice || '',
            _insured_variant_id: variantId,
            _insurance_ref: ref,
            _insured_line: Date.now().toString() + i,
            ...(this.dataset.isBundle === 'true' ? {
              _is_bundle: 'true',
              _bundle_components: this.dataset.bundleComponents || '',
            } : {}),
          },
        });
      }

      const body = JSON.stringify({
        items,
        ...(cartItemComponentsSectionIds.length > 0 ? { sections: cartItemComponentsSectionIds.join(',') } : {}),
      });

      fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.dispatchEvent(new CartErrorEvent('', response.message, response.description, response.errors));

            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                didError: true,
                source: 'insurance-component',
                itemCount: items.length,
                productId: insuranceProductId,
              }),
            );

            return;
          } else {
            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                source: 'insurance-component',
                itemCount: items.length,
                productId: insuranceProductId,
                sections: response.sections,
              }),
            );
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.refs.checkbox.disabled = false;
        });
    } else {
      const insuranceItemsKeys = this.dataset.insuranceItemsKeys.split(',');
      const updates = insuranceItemsKeys.reduce((acc, key) => {
        acc[key] = 0;
        return acc;
      }, {});

      const body = JSON.stringify({
        updates,
        ...(cartItemComponentsSectionIds.length > 0 ? { sections: cartItemComponentsSectionIds.join(',') } : {}),
      });

      fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.dispatchEvent(new CartErrorEvent('', response.message, response.description, response.errors));

            this.dispatchEvent(
              new CartUpdateEvent(response, 'insurance-component', {
                source: 'insurance-component',
                itemCount: response.item_count,
              }),
            );

            return;
          } else {
            this.dispatchEvent(
              new CartUpdateEvent(response, 'insurance-component', {
                source: 'insurance-component',
                itemCount: response.item_count,
              }),
            );
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.refs.checkbox.disabled = false;
        });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.checkbox.disabled = false;
  }
}

if (!customElements.get('insurance-component')) customElements.define('insurance-component', InsuranceComponent);

let insuranceSyncInFlight = false;

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  if (!(event instanceof CartUpdateEvent) && !(event instanceof CartAddEvent)) return;
  if (event.detail.data?.didError) return;
  if (event.detail.data?.source === 'insurance-component') return;
  if (event.detail.data?.source === 'insurance-sync') return;
  if (insuranceSyncInFlight) return;

  const cart = await (await fetch('/cart.json')).json();

  const insuranceGroups = {};
  for (const item of cart.items) {
    const insuredVariantId = item.properties?._insured_variant_id;
    if (!insuredVariantId) continue;
    (insuranceGroups[insuredVariantId] ||= []).push(item);
  }

  if (Object.keys(insuranceGroups).length === 0) return;

  const insuredQuantities = {};
  for (const item of cart.items) {
    const variantIdStr = String(item.variant_id);
    if (insuranceGroups[variantIdStr]) {
      insuredQuantities[variantIdStr] = (insuredQuantities[variantIdStr] || 0) + item.quantity;
    }
  }

  const updates = {};
  const itemsToAdd = [];

  for (const [insuredVariantId, insuranceItems] of Object.entries(insuranceGroups)) {
    const insuredQty = insuredQuantities[insuredVariantId] || 0;
    const insuranceCount = insuranceItems.length;

    if (insuredQty === 0) {
      for (const item of insuranceItems) updates[item.key] = 0;
    } else if (insuranceCount > insuredQty) {
      const excess = insuranceCount - insuredQty;
      for (let i = 0; i < excess; i++) updates[insuranceItems[i].key] = 0;
    } else if (insuranceCount < insuredQty) {
      const insuranceVariantId = insuranceItems[0].variant_id;
      const toAdd = insuredQty - insuranceCount;
      // Get data from the first existing insurance item's properties
      const firstItem = insuranceItems[0];
      for (let i = 0; i < toAdd; i++) {
        const ref = `ins-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        itemsToAdd.push({
          id: insuranceVariantId,
          quantity: 1,
          properties: {
            _insure: 'true',
            _insurer: firstItem.properties?._insurer || '',
            _insurance_premium: firstItem.properties?._insurance_premium || '',
            _insurance_type: firstItem.properties?._insurance_type || 'regular',
            _insured_product_title: firstItem.properties?._insured_product_title || '',
            _insured_product_brand: firstItem.properties?._insured_product_brand || '',
            _insured_product_price: firstItem.properties?._insured_product_price || '',
            _insured_variant_id: insuredVariantId,
            _insurance_ref: ref,
            _insured_line: Date.now().toString() + i,
          },
        });
      }
    }
  }

  if (Object.keys(updates).length === 0 && itemsToAdd.length === 0) return;

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const cartItemComponentsSectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
  });
  const sectionsParam = cartItemComponentsSectionIds.length > 0 ? cartItemComponentsSectionIds.join(',') : null;

  insuranceSyncInFlight = true;

  try {
    let lastResponse = null;

    if (Object.keys(updates).length > 0) {
      const body = JSON.stringify({
        updates,
        ...(sectionsParam ? { sections: sectionsParam } : {}),
      });
      lastResponse = await (await fetch(Theme.routes.cart_update_url, { ...fetchConfig('json', { body }) })).json();
    }

    if (itemsToAdd.length > 0) {
      const body = JSON.stringify({
        items: itemsToAdd,
        ...(sectionsParam ? { sections: sectionsParam } : {}),
      });
      lastResponse = await (await fetch(Theme.routes.cart_add_url, { ...fetchConfig('json', { body }) })).json();
    }

    if (lastResponse) {
      document.dispatchEvent(
        new CartUpdateEvent(lastResponse, 'insurance-sync', {
          source: 'insurance-sync',
          itemCount: lastResponse.item_count,
        }),
      );
    }
  } catch (error) {
    console.error(error);
  } finally {
    insuranceSyncInFlight = false;
  }
});
